/*
 * Copyright (c) Nexign, JSC, 2024.
 */
/**
 *
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.30.0
 */

import { Fragment, useCallback, useMemo } from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { EmailInput } from '@/main/components/common/EmailInput/EmailInput';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardCollationReport,
} from '@/main/modules/internationalization/intSelectors';
import { ICommonTranslation } from '@/main/modules/internationalization/types';
import { IWizardCollationReportTranslation } from '@/main/modules/internationalization/types/wizards/collationReport';
import { WizardReportHeaderStyled } from '@/main/modules/wizard/components/WizardReport/styled/WizardReportStyled';
import WizardReport from '@/main/modules/wizard/components/WizardReport/WizardReport';
import { EWizardReportParam } from '@/main/modules/wizard/components/WizardReport/WizardReportConstants';
import { WizardReportParam } from '@/main/modules/wizard/components/WizardReport/WizardReportParam';
import { makeSelectWizardReportParam } from '@/main/modules/wizard/components/WizardReport/wizardReportSelectors';
import { IWizardReportParam } from '@/main/modules/wizard/components/WizardReport/wizardReportTypes';
import WizardSummary, { IWizardSummaryParam } from '@/main/modules/wizard/components/WizardSummary/WizardSummary';
import { wizardReportSetParamAction } from '@/main/modules/wizard/wizardActions';
import { IWizardReportSetParamPayload } from '@/main/modules/wizard/wizardTypes';
import { IRootReducer } from '@/main/reducers/rootReducer';

import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import { TWizardCollationReportSetParamsPayload } from '../types/wizardCollationReportActionTypes';
import { wizardCollationReportSetParamsAction } from '../wizardCollationReportActions';
import { EWizardCollationReportSteps } from '../wizardCollationReportConstant';
import { IWizardCollationReportReducer } from '../wizardCollationReportReducer';
import {
    makeSelectWizardCollationReportIsStepValid,
    makeSelectWizardCollationReportParams,
} from '../wizardCollationReportSelectors';
import { WizardCollationReportStepConfirmationStyled } from './styled/WizardCollationReportStyled';

const WizardCollationReportStepConfirm = ({
    setNotificationParam,
    wizardCollationReportState,
    reportDestEmail,
    translation,
    translationCommon,
}: IConnectedProps & IDispatchProps) => {
    const stepTranslation = translation[EWizardCollationReportSteps.ConfirmStep];

    const handleChange = useCallback(
        (payload: IWizardReportSetParamPayload) => {
            setNotificationParam(payload);
            // checkValidation();
        },
        [setNotificationParam],
    );

    const additionalReportsSummaryParams: IWizardSummaryParam[] = useMemo(
        () => [
            {
                title: stepTranslation.summary.params.title,
                values: [
                    `${stepTranslation.summary.params.startDateTitle} ${moment(
                        wizardCollationReportState.startDate,
                    ).format(EFormatData.FullData)}`,
                    `${stepTranslation.summary.params.endDateTitle} ${moment(wizardCollationReportState.endDate).format(
                        EFormatData.FullData,
                    )}`,
                ],
            },
            {
                title: stepTranslation.summary.notify.title,
                values: [wizardCollationReportState.reportFormat],
            },
        ],
        [
            stepTranslation.summary.notify.title,
            stepTranslation.summary.params.endDateTitle,
            stepTranslation.summary.params.startDateTitle,
            stepTranslation.summary.params.title,
            wizardCollationReportState.endDate,
            wizardCollationReportState.reportFormat,
            wizardCollationReportState.startDate,
        ],
    );

    return (
        <WizardCollationReportStepConfirmationStyled>
            <WizardReport notificationValidate={() => {}}>
                <Fragment>
                    <WizardReportHeaderStyled>Отправить документы</WizardReportHeaderStyled>

                    <WizardReportParam
                        isEnabled={reportDestEmail.isEnabled}
                        label={stepTranslation.reports.destEmailForLink}
                        setEnabled={(isEnabled: boolean) =>
                            handleChange({
                                paramType: EWizardReportParam.EMAIL_DEST,
                                isEnabled,
                            })
                        }
                    >
                        <EmailInput
                            emailInputValue={reportDestEmail.value}
                            isValid={reportDestEmail.isEnabled}
                            translation={translationCommon}
                            onEmailInputChange={(value: string) =>
                                handleChange({
                                    paramType: EWizardReportParam.EMAIL_DEST,
                                    value,
                                })
                            }
                            onEmailInputValidate={(isValid: boolean) =>
                                handleChange({
                                    paramType: EWizardReportParam.EMAIL_DEST,
                                    isValid,
                                })
                            }
                        />
                    </WizardReportParam>
                </Fragment>
            </WizardReport>

            <div>
                <WizardSummary dataName='wizard-collation-report__summary-info'
                               params={additionalReportsSummaryParams} />
            </div>
        </WizardCollationReportStepConfirmationStyled>
    );
};

export interface IConnectedProps {
    reportDestEmail: IWizardReportParam;
    isStepValid: boolean;
    wizardCollationReportState: IWizardCollationReportReducer;
    translation: IWizardCollationReportTranslation;
    translationCommon: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isStepValid: makeSelectWizardCollationReportIsStepValid(),
    wizardCollationReportState: makeSelectWizardCollationReportParams(),
    reportDestEmail: makeSelectWizardReportParam(EWizardReportParam.EMAIL_DEST),
    translation: makeSelectTranslationWizardCollationReport(),
    translationCommon: makeSelectTranslationCommon(),
});

export interface IDispatchProps {
    setParams: (payload: TWizardCollationReportSetParamsPayload) => void;
    setNotificationParam: (payload: IWizardReportSetParamPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setParams: (payload) => dispatch(wizardCollationReportSetParamsAction(payload)),
    setNotificationParam: (payload) => dispatch(wizardReportSetParamAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardCollationReportStepConfirm);
